import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as submissionSelector from "store/selectors/submission/submissionSelector";

const ENGINE = "united_es_auto_v1";

// Inputs that drive the engine's auto hazard grade (owned fleet + state + usage +
// setting). HNOA is intentionally excluded — it doesn't affect the grade.
const GRADE_INPUT_KEYS = [
  "vehiclePpt",
  "vehicleLightTruck",
  "vehicleMediumTruck",
  "vehicleHeavyTruck",
  "vehicleExtraHeavyTruck",
  "vehicleHeavyTruckTractor",
  "vehicleExtraHeavyTruckTractor",
  "vehiclePassengerTransport",
  "vehicleOther",
  "predominantState",
  "vehicleUsage",
  "setting",
];

// Live "derived" auto hazard grade. Rather than reimplement the workbook's B24
// composite (and its 50-state table) in JS — a second copy that could drift from
// the engine — we ask the engine, the single source of truth, on a debounce
// whenever the grade-driving inputs change. Returns null until the first result.
const useDerivedAutoHazardGrade = (data) => {
  const submission = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );
  const [trigger] = analyticsService.useLazySubmissionAnalyticsQuery();
  const [grade, setGrade] = useState(null);

  // Keep the latest submission without making it a recompute trigger — only the
  // grade-driving inputs should fire a new call.
  const submissionRef = useRef(submission);
  submissionRef.current = submission;

  const inputKey = JSON.stringify(
    GRADE_INPUT_KEYS.map((k) => data?.[k] ?? null)
  );
  const debouncedKey = useDebouncedValue(inputKey, { delayMs: 400 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await trigger({
          engine: ENGINE,
          metric: "program_pricing",
          submission: submissionRef.current,
        });
        const next = response?.data?.failure
          ? null
          : response?.data?.values?.inputs?.auto_hazard_grade ?? null;
        if (!cancelled) setGrade(next);
      } catch {
        if (!cancelled) setGrade(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedKey, trigger]);

  return grade;
};

export default useDerivedAutoHazardGrade;
